<script setup lang="ts">
import {
  DEFAULT_EMI_TENURES,
  DEFAULT_EMI_TENURE,
} from "~/utils/emiCalculator";

const props = withDefaults(
  defineProps<{
    modelValue: "full" | "monthly";
    tenure?: number;
    availableTenures?: number[];
    defaultTenure?: number;
  }>(),
  {
    modelValue: "full",
    tenure: DEFAULT_EMI_TENURE,
    availableTenures: () => [...DEFAULT_EMI_TENURES],
    defaultTenure: DEFAULT_EMI_TENURE,
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: "full" | "monthly"): void;
  (e: "update:tenure", value: number): void;
}>();

function setMode(targetMode: "full" | "monthly") {
  emit("update:modelValue", targetMode);
  if (targetMode === "full") {
    emit("update:tenure", props.defaultTenure);
  }
}

function handleModeKeydown(e: KeyboardEvent, targetMode: "full" | "monthly") {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    setMode(targetMode);
  } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
    e.preventDefault();
    setMode("full");
  } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
    e.preventDefault();
    setMode("monthly");
  }
}

function handleTenureKeydown(e: KeyboardEvent, currentIndex: number) {
  const tenures = props.availableTenures;
  if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
    e.preventDefault();
    const prevIndex = (currentIndex - 1 + tenures.length) % tenures.length;
    emit("update:tenure", tenures[prevIndex]!);
  } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
    e.preventDefault();
    const nextIndex = (currentIndex + 1) % tenures.length;
    emit("update:tenure", tenures[nextIndex]!);
  }
}
</script>

<template>
  <div class="flex flex-wrap items-center gap-2.5">
    <!-- Mode Segmented Control -->
    <div
      role="radiogroup"
      aria-label="Payment option"
      class="inline-flex items-center rounded-[6px] border border-on-primary-border bg-button-secondary/80 p-0.5 relative backdrop-blur-xs"
    >
      <button
        type="button"
        role="radio"
        :aria-checked="modelValue === 'full'"
        tabindex="0"
        @click="setMode('full')"
        @keydown="handleModeKeydown($event, 'full')"
        :class="[
          'px-3.5 py-1 text-xs font-semibold rounded-[6px] transition-all duration-200 cursor-pointer outline-none select-none',
          modelValue === 'full'
            ? 'bg-button-primary text-button-on-primary shadow-xs'
            : 'text-on-primary-body hover:text-on-primary-heading'
        ]"
      >
        Pay in full
      </button>

      <button
        type="button"
        role="radio"
        :aria-checked="modelValue === 'monthly'"
        tabindex="0"
        @click="setMode('monthly')"
        @keydown="handleModeKeydown($event, 'monthly')"
        :class="[
          'px-3.5 py-1 text-xs font-semibold rounded-[6px] transition-all duration-200 cursor-pointer outline-none select-none flex items-center gap-1.5',
          modelValue === 'monthly'
            ? 'bg-button-primary text-button-on-primary shadow-xs'
            : 'text-on-primary-body hover:text-on-primary-heading'
        ]"
      >
        <span>Pay monthly</span>
        <span
          :class="[
            'text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-[4px] tracking-wider',
            modelValue === 'monthly'
              ? 'bg-white/20 text-white'
              : 'bg-emerald-600/10 text-emerald-700'
          ]"
        >
          No Cost EMI
        </span>
      </button>
    </div>

    <!-- Tenure Selector -->
    <Transition name="tenure-expand">
      <div
        v-if="modelValue === 'monthly'"
        role="radiogroup"
        aria-label="EMI tenure in months"
        class="tenure-container inline-flex items-center rounded-[6px] border border-on-primary-border bg-button-secondary/80 p-0.5 relative overflow-hidden"
      >
        <button
          v-for="(t, idx) in availableTenures"
          :key="t"
          type="button"
          role="radio"
          :aria-checked="tenure === t"
          :tabindex="tenure === t ? 0 : -1"
          @click="emit('update:tenure', t)"
          @keydown="handleTenureKeydown($event, idx)"
          :class="[
            'px-2.5 py-1 text-xs font-semibold rounded-[6px] transition-all duration-200 cursor-pointer outline-none select-none active:scale-95',
            tenure === t
              ? 'bg-button-primary text-button-on-primary shadow-xs'
              : 'text-on-primary-body hover:text-on-primary-heading'
          ]"
        >
          {{ t }}m
        </button>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.tenure-container {
  max-width: 300px;
  white-space: nowrap;
}

.tenure-expand-enter-active {
  transition: max-width 320ms cubic-bezier(0.16, 1, 0.3, 1),
              opacity 240ms cubic-bezier(0.16, 1, 0.3, 1),
              transform 320ms cubic-bezier(0.16, 1, 0.3, 1),
              filter 240ms ease;
}

.tenure-expand-leave-active {
  transition: max-width 240ms cubic-bezier(0.7, 0, 0.84, 0),
              opacity 180ms ease,
              transform 240ms cubic-bezier(0.7, 0, 0.84, 0),
              filter 180ms ease;
}

.tenure-expand-enter-from,
.tenure-expand-leave-to {
  max-width: 0 !important;
  opacity: 0;
  transform: scale(0.9) translateX(-10px);
  filter: blur(4px);
  padding-left: 0 !important;
  padding-right: 0 !important;
  border-width: 0 !important;
}

.tenure-expand-enter-to,
.tenure-expand-leave-from {
  max-width: 300px;
  opacity: 1;
  transform: scale(1) translateX(0);
  filter: blur(0);
}
</style>
